import { type OperationVariables } from '@apollo/react-common';
import { type MutationOptions } from 'apollo-client';
import { type GraphQLTaggedNode } from 'react-relay';
import { type FetchResult } from 'apollo-link';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { commitMutation } from './commitMutation.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { apolloCompatibleMutation } from './apolloCompatibleMutation.ts';
import { convertToApolloError } from './query.ts';
// Package-local wrapper — surfaces refetch errors as `ApolloErrorInterop`.
import { fetchQuery } from './fetchQuery.ts';

export type MutateInteropSupportedResult<TData> = Pick<FetchResult<TData>, 'data' | 'errors'>;

// Subset of fields from Apollo's MutationOptions that are supported by mutateInterop.
// Note: on Relay, the `refetchQueries` function-form callback and the `update` cache-writer
// callback only receive `data` / `errors` — `context` / `extensions` are undefined, and the
// `DataProxy` argument to `update` is passed as `null`. Apollo passes the full shapes natively.
export type MutateInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	MutationOptions<TData, TVariables>,
	| 'variables'
	| 'errorPolicy'
	| 'optimisticResponse'
	| 'update'
	| 'refetchQueries'
	| 'awaitRefetchQueries'
> & {
	mutation: GraphQLTaggedNode;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function mutateInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: MutateInteropSupportedArgs<TData, TVariables>,
): Promise<MutateInteropSupportedResult<TData>> {
	return isRelayEnabled(params.__callerPackage__)
		? mutateInteropRelay<TData, TVariables>(params)
		: mutateInteropApollo<TData, TVariables>(params);
}

const stringNameRefetchError = (entry: string) =>
	new Error(
		`[Relay migration] refetchQueries with string query names is not supported in the Relay variant. Pass the GraphQL tagged node (graphql\`query ...\`) instead of the string "${entry}".`,
	);

function mutateInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: MutateInteropSupportedArgs<TData, TVariables>,
): Promise<MutateInteropSupportedResult<TData>> {
	const { refetchQueries, awaitRefetchQueries, optimisticResponse, update } = params;

	// Static-array string entries fail eagerly; function-form is checked post-response.
	if (Array.isArray(refetchQueries)) {
		const stringEntry = refetchQueries.find((entry) => typeof entry === 'string');
		if (stringEntry) {
			throw stringNameRefetchError(stringEntry);
		}
	}

	const env = window.GLOBAL_RELAY_ENV!;
	const operationVariables = params.variables ?? ({} as TVariables);

	// Apollo parity: resolve optimisticResponse and apply it to the caller's cache before the
	// mutation begins. The Relay `commitMutation` will additionally apply this to its own store.
	let resolvedOptimisticResponse: TData | undefined;
	try {
		if (optimisticResponse != null) {
			resolvedOptimisticResponse =
				typeof optimisticResponse === 'function'
					? (optimisticResponse as (vars: TVariables) => TData)(operationVariables)
					: (optimisticResponse as TData);
			update?.(null as never, { data: resolvedOptimisticResponse } as FetchResult<TData>);
		}
	} catch (err) {
		return Promise.reject(err);
	}

	const rollbackOptimistic = () => {
		if (resolvedOptimisticResponse !== undefined && update) {
			try {
				update(null as never, { data: undefined } as FetchResult<TData>);
			} catch {
				// ignore rollback failure — we're already on the error path
			}
		}
	};

	return apolloCompatibleMutation<TData>({
		fetchData: () =>
			commitMutation(env, {
				mutation: params.mutation,
				variables: operationVariables,
				optimisticResponse: resolvedOptimisticResponse as any,
			}) as Promise<TData>,
		errorPolicy: params.errorPolicy,
		convertToApolloError,
		onError: () => {
			rollbackOptimistic();
		},
	}).then((mutationResult) => {
		// Apply `update` with real data outside `apolloCompatibleMutation`'s try/catch so a throw
		// here rejects the returned promise *without* re-entering the rollback path (the mutation
		// already committed on the server). Skip when `data` is undefined (`errorPolicy: 'all' |
		// 'ignore'` recovered from a mutation error — rollback already ran via `onError`).
		if (mutationResult.data !== undefined) {
			update?.(null as never, { data: mutationResult.data } as FetchResult<TData>);
		}

		// `== null` covers both `undefined` (per the Apollo type) and `null` (callers using `as any`).
		if (refetchQueries == null) {
			return mutationResult;
		}

		// Refetches run whenever `apolloCompatibleMutation` returned a result (including
		// `errorPolicy: 'all' | 'ignore'` recoveries) — matches Apollo, which still runs
		// refetchQueries with partial data/errors. With the default `errorPolicy: 'none'`, a
		// mutation error would have thrown, so this `.then` never executes.

		// See the note on `MutateInteropSupportedArgs` — only `data` / `errors` are exposed here.
		const resolvedEntries =
			typeof refetchQueries === 'function'
				? refetchQueries({
						data: mutationResult.data,
						errors: mutationResult.errors,
					} as FetchResult<TData>)
				: refetchQueries;

		const stringEntry = (resolvedEntries as unknown[]).find(
			(entry): entry is string => typeof entry === 'string',
		);
		if (stringEntry) {
			throw stringNameRefetchError(stringEntry);
		}

		const refetchPromises = (
			resolvedEntries as unknown as Array<{ query?: GraphQLTaggedNode; variables?: any }>
		).map((entry) => {
			// `GraphQLTaggedNode` covers both `ConcreteRequest` (kind: 'Request') and `ReaderFragment`
			// (kind: 'Fragment'); only the former is valid for `fetchQuery`.
			if (!entry || !entry.query || (entry.query as any).kind !== 'Request') {
				throw new Error(
					`[Relay migration] refetchQueries entries must be \`{ query: graphql\`query ...\`, variables? }\` (a query, not a fragment). Received: ${JSON.stringify(entry)}`,
				);
			}
			return fetchQuery(env, entry.query, entry.variables ?? {}, {
				fetchPolicy: 'network-only',
			}).toPromise();
		});

		// Refetch errors always reject (with `awaitRefetchQueries`) — `errorPolicy` only covers the mutation.
		if (awaitRefetchQueries && refetchPromises.length > 0) {
			return Promise.all(refetchPromises).then(() => mutationResult);
		}
		// Apollo Client v2 effectively drops fire-and-forget refetch promises (`Promise.all([])` —
		// see QueryManager.mutate). We attach a per-promise catch so every failure is observable.
		refetchPromises.forEach((p) => {
			p.catch((refetchError) => {
				// eslint-disable-next-line no-console
				console.warn('[Relay migration] refetchQueries error:', refetchError);
			});
		});
		return mutationResult;
	});
}

function mutateInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>({
	mutation,
	...rest
}: MutateInteropSupportedArgs<TData, TVariables>): Promise<MutateInteropSupportedResult<TData>> {
	return window['GLOBAL_APOLLO_CLIENT']!.mutate({
		...rest,
		mutation: toDocumentNode(mutation),
	});
}
